const symbols = {
    times: '×', div: '÷', cdot: '·', pm: '±', mp: '∓', le: '≤', leq: '≤', ge: '≥', geq: '≥',
    ne: '≠', neq: '≠', approx: '≈', equiv: '≡', pi: 'π', theta: 'θ', alpha: 'α', beta: 'β',
    gamma: 'γ', delta: 'δ', Delta: 'Δ', infinity: '∞', infty: '∞', degree: '°', circ: '°',
    angle: '∠', perp: '⊥', parallel: '∥', percent: '%'
};
const fractions = { '½': ['1', '2'], '⅓': ['1', '3'], '⅔': ['2', '3'], '¼': ['1', '4'], '¾': ['3', '4'], '⅕': ['1', '5'], '⅖': ['2', '5'], '⅗': ['3', '5'], '⅘': ['4', '5'], '⅙': ['1', '6'], '⅚': ['5', '6'], '⅛': ['1', '8'], '⅜': ['3', '8'], '⅝': ['5', '8'], '⅞': ['7', '8'] };
const textNode = (text) => ({ type: 'text', text });
const fractionNode = (numerator, denominator) => ({ type: 'fraction', numerator: [textNode(numerator)], denominator: [textNode(denominator)] });

function reader(source) {
    let position = 0;
    const skipSpaces = () => { while (/\s/.test(source[position] || '') && position < source.length) position += 1; };
    const group = (depth) => {
        skipSpaces();
        if (source[position] !== '{') throw new Error('Expected grouped expression');
        position += 1;
        const result = sequence(depth + 1, '}');
        if (!result.length) throw new Error('Empty expression');
        return result;
    };
    const atom = (depth) => {
        if (depth > 24) throw new Error('Expression too deeply nested');
        const character = source[position++];
        if (!character) throw new Error('Missing expression');
        if (character === '{') {
            position -= 1;
            return { type: 'group', children: group(depth) };
        }
        if (character === '}') throw new Error('Unexpected closing brace');
        if (fractions[character]) return fractionNode(...fractions[character]);
        if (/\d/.test(character)) {
            const number = /^\d+(?:[.,]\d+)?/.exec(source.slice(position - 1))[0];
            position += number.length - 1;
            return textNode(number);
        }
        if (character !== '\\') return textNode(character === '-' ? '−' : character);
        const command = /^[a-zA-Z]+|[%{}_,;! ]/.exec(source.slice(position))?.[0];
        if (!command) throw new Error('Missing command');
        position += command.length;
        if (['frac', 'dfrac', 'tfrac'].includes(command)) return { type: 'fraction', numerator: group(depth), denominator: group(depth) };
        if (command === 'sqrt') return { type: 'root', children: group(depth) };
        if (['text', 'mathrm'].includes(command)) {
            skipSpaces();
            const literal = /^\{([^{}]*)\}/.exec(source.slice(position));
            if (!literal) throw new Error('Unsupported text group');
            position += literal[0].length;
            return textNode(literal[1]);
        }
        if (symbols[command]) return textNode(symbols[command]);
        if (['left', 'right'].includes(command)) return textNode('');
        if ([',', ';', ' ', 'quad', 'qquad'].includes(command)) return textNode(' ');
        if (command === '!') return textNode('');
        if (['%', '{', '}', '_'].includes(command)) return textNode(command);
        throw new Error('Unsupported command');
    };
    const sequence = (depth, closing) => {
        const nodes = [];
        while (position < source.length && source[position] !== closing) {
            if (source[position] === '^' || source[position] === '_') {
                const kind = source[position++] === '^' ? 'superscript' : 'subscript';
                const base = nodes.pop();
                if (!base) throw new Error('Missing script base');
                skipSpaces();
                const script = source[position] === '{' ? group(depth) : [atom(depth + 1)];
                nodes.push({ type: kind, children: [base], script });
            } else {
                nodes.push(atom(depth));
            }
        }
        if (closing) {
            if (source[position] !== closing) throw new Error('Unclosed group');
            position += 1;
        }
        return nodes;
    };
    return { all: () => sequence(0), one: () => ({ nodes: [atom(0)], length: position }) };
}

function plainSegments(source) {
    const segments = [];
    let start = 0;
    let position = 0;
    while (position < source.length) {
        let match;
        let nodes;
        let length = 0;
        const remaining = source.slice(position);
        if (source[position] === '\\') {
            try {
                const parsed = reader(remaining).one();
                nodes = parsed.nodes;
                length = parsed.length;
            } catch { nodes = undefined; }
        } else if (fractions[source[position]]) {
            nodes = [fractionNode(...fractions[source[position]])];
            length = 1;
        } else if (!/[\w/.:]/.test(source[position - 1] || '') && (match = /^(\d+)\s*[/⁄]\s*(\d+)(?![\w/]|\.\d)/.exec(remaining)) && !/[\w/]/.test(source[position + match[0].length] || '')) {
            nodes = [fractionNode(match[1], match[2])];
            length = match[0].length;
        } else if (!/\w/.test(source[position - 1] || '') && (match = /^(?:(?:[-+]?\d+(?:\.\d+)?\s*)?[A-Za-z]+|[-+]?\d+(?:\.\d+)?|\([^()\n]+\))\^(?:\{[^{}\n]+\}|[-+]?\d+|[A-Za-z])(?!\w)/.exec(remaining))) {
            nodes = reader(match[0]).all();
            length = match[0].length;
        }
        if (length) {
            if (position > start) segments.push({ type: 'text', text: source.slice(start, position) });
            segments.push({ type: 'math', nodes });
            position += length;
            start = position;
        } else position += 1;
    }
    if (start < source.length) segments.push({ type: 'text', text: source.slice(start) });
    return segments;
}

export function parseMathText(value) {
    const source = String(value ?? '');
    const result = [];
    const delimiters = /\\\(([\s\S]*?)\\\)|\\\[([\s\S]*?)\\\]|\$\$([\s\S]*?)\$\$|\$([^$\n]+)\$/g;
    let start = 0;
    for (const match of source.matchAll(delimiters)) {
        result.push(...plainSegments(source.slice(start, match.index)));
        const expression = match.slice(1).find((part) => part !== undefined);
        try {
            if (/\d\s+[a-zA-Z]{2,}/.test(expression)) throw new Error('Possible prose or currency');
            result.push({ type: 'math', nodes: reader(expression).all() });
        } catch {
            result.push({ type: 'text', text: match[0] });
        }
        start = match.index + match[0].length;
    }
    result.push(...plainSegments(source.slice(start)));
    return result;
}
